import UserAvatar20 from '@carbon/icons-react/es/user--avatar/20';
import { Tooltip } from 'antd';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { Company, ProjectId } from 'api/models';
import { ReactComponent as ExportUserIcon } from 'assets/svgs/export-user.svg';
import { Avatar, AvatarGroup } from 'components/atoms/avatar';
import { Button } from 'components/atoms/button';
import {
  ProjectAssigneePicker,
  ProjectAssigneePickerProps,
} from 'components/molecules/assignee-picker';
import { Trigger } from 'components/molecules/squad-picker/trigger';
import { Option } from 'components/molecules/squad-picker/types';
import {
  optionsToSquadPayload,
  squadToOptions,
} from 'components/molecules/squad-picker/utils';
import { useIconTypeContext } from 'contexts/icon-type';
import { Task } from 'data/tasks';
import {
  UpdateTaskActionPayloadOptions,
  updateTaskSagaAction,
} from 'data/tasks/actions';
import { useTranslate } from 'hooks/use-translation';
import { TaskFooterAdditionalDetails } from '../footer-additional-details';
import {
  CardFooter,
  CardFooterItem,
  SubTaskExpander,
} from './task-card-footer.styles';

const StyledButton = styled(Button)`
  & > svg {
    margin-right: 0;
  }
  color: var(--link-color);
  height: var(--input-size-sm);
  padding-top: 0;
  padding-bottom: 0;
`;

const ProjectAssigneeRenderer: FC<ProjectAssigneePickerProps> = ({
  value,
  projectId,
  isPrivate,
  onDone,
  startDate,
  dueDate,
}) => {
  const [visible, setVisible] = useState(false);
  const onFinish = useCallback<
    NonNullable<ProjectAssigneePickerProps['onDone']>
  >(
    (options, callback) => {
      onDone?.(options, callback);
      setVisible(false);
    },
    [onDone],
  );

  const { isExportUI } = useIconTypeContext();

  const handleShowAssigneePicker = useCallback(() => {
    setVisible(true);
  }, []);

  const handleHideAssigneePicker = useCallback(() => {
    setVisible(false);
  }, []);

  return (
    <>
      {isExportUI ? (
        <ExportUserIcon />
      ) : (
        <>
          <StyledButton
            onClick={handleShowAssigneePicker}
            type="text"
            icon={(value ?? []).length > 0 ? null : <UserAvatar20 />}
          >
            <Trigger size="small" isMini value={value || []} />
          </StyledButton>
          {visible ? (
            <ProjectAssigneePicker
              projectId={projectId}
              isPrivate={isPrivate}
              value={value}
              onDone={onFinish}
              onSave={onFinish}
              onClose={handleHideAssigneePicker}
              isMini
              hideTrigger
              startDate={startDate}
              dueDate={dueDate}
            />
          ) : null}
        </>
      )}
    </>
  );
};

export const TaskCardFooter = ({
  task,
  onSubTaskToggle,
  subTaskVisible,
  subTasks,
}: {
  task: Task;
  onSubTaskToggle: (value: boolean) => void;
  subTaskVisible: boolean;
  subTasks: Task[];
}) => {
  const translate = useTranslate();
  const dispatch = useDispatch();
  const projectId = useMemo(() => ProjectId(task.project), [task.project]);
  const squadOptions = useMemo(
    () => (task.assignee ? squadToOptions(task.assignee) : []),
    [task.assignee],
  );

  const updateTask = useCallback(
    (payload, options?: UpdateTaskActionPayloadOptions) => {
      dispatch(
        updateTaskSagaAction({
          projectId: task.project,
          taskId: task.taskId,
          data: payload,
          options,
        }),
      );
    },
    [dispatch, task.project, task.taskId],
  );

  const handleAssigneeChange = useCallback(
    (assignee: Option[], resolveCallback?: (value) => void) => {
      return updateTask(
        { assignee: optionsToSquadPayload(assignee) },
        { resolveCallback },
      );
    },
    [updateTask],
  );

  const responsibleCompaniesTitle = (task.responsible ?? [])
    .map((company) => company.companyName)
    .join(', ');

  return (
    <CardFooter onClick={(e) => e.stopPropagation()}>
      <CardFooterItem className="assignee-picker">
        {task.assignee && (
          <span data-cy={`task-card.${task.taskName}.assignee_trigger`}>
            <ProjectAssigneeRenderer
              projectId={projectId}
              isPrivate={task.private}
              value={squadOptions}
              onDone={handleAssigneeChange}
              onSave={handleAssigneeChange}
              isMini
              startDate={task.startDate}
              dueDate={task.dueDate}
            />
          </span>
        )}
      </CardFooterItem>
      <CardFooterItem className="subtask_count">
        <SubTaskExpander
          data-cy="SubTaskExpander"
          onClick={() => onSubTaskToggle(!subTaskVisible)}
          type="text"
          size="small"
          active={subTaskVisible}
        >
          <TaskFooterAdditionalDetails task={task} subTasks={subTasks} />
        </SubTaskExpander>
      </CardFooterItem>
      <CardFooterItem className="responsible_avatar">
        <Tooltip
          title={translate('new_task.responsible', {
            companyName: responsibleCompaniesTitle,
          })}
        >
          <div>
            <AvatarGroup maxCount={2}>
              {(task.responsible ?? []).map((company: Company) => (
                <Avatar
                  size="small"
                  key={company.companyId}
                  name={company.companyName}
                  src={company.companyLogoUrl}
                />
              ))}
            </AvatarGroup>
          </div>
        </Tooltip>
      </CardFooterItem>
    </CardFooter>
  );
};
